import React, { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { PublishingArtist } from 'types/publishing';
import { ui } from '../ui';

interface Props {
  artist: PublishingArtist;
  readOnly: boolean;
  onNotMe: () => void;
}

export function PublishingHeader({ artist, readOnly, onNotMe }: Props) {
  const { t } = useTranslation('publishing');
  const usable = artist.identity === 'usable' && !!artist.ipi;
  const [open, setOpen] = useState(!usable);
  useEffect(() => setOpen(!usable), [artist.spotifyArtistId, usable]);

  return (
    <div>
      <h1 className={`m-0 mb-1 ${ui.title}`}>{artist.artistName}</h1>
      <p className={`m-0 ${ui.body}`}>
        {artist.legalName
          ? t('header_songwriter', { name: artist.legalName })
          : t('header_songwriter_missing')}
      </p>
      <div className="mt-2 flex flex-col items-start gap-1">
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 bg-transparent p-0 ${ui.muted}`}
          onClick={() => setOpen(!open)}
          aria-expanded={open}
        >
          <ChevronRight
            size={14}
            style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}
          />
          {t('header_disclose')}
        </button>
        {open &&
          (usable ? (
            <span className={`inline-flex flex-wrap items-baseline gap-x-2 ${ui.muted}`}>
              <span className="tabular-nums tracking-[0.03em]">
                {[artist.writerName, artist.ipi, artist.pro].filter(Boolean).join(' · ')}
              </span>
              {/* Escape hatch for a wrong unique match (FR-02b); a missing or
                  ambiguous ID is handled by the alert tile instead. */}
              <button
                type="button"
                className={`${ui.link} disabled:cursor-not-allowed disabled:opacity-50`}
                disabled={readOnly}
                onClick={onNotMe}
              >
                {t('header_not_me')}
              </button>
            </span>
          ) : (
            <span className="text-base text-red-600">{t('header_not_set_up')}</span>
          ))}
      </div>
    </div>
  );
}
